/**
 * Bundle 161 - Admin surface for incomplete profiles.
 * Manual per-user + bulk email reminder (v1 -> 48h -> v2 -> delete)
 * escalation flow. NO auto-cron per user spec.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Mail, MailWarning, Trash2, Users, ExternalLink, Wrench, Eye, EyeOff } from 'lucide-react';
import { VerdictChip, VerdictLegend } from '@/components/VerdictChip';
import { formatRelativeTime, isWithin24hCooldown } from '@/utils/relativeTime';
import { useAdminBadge } from '@/context/AdminBadgeContext';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'LEAK', label: 'Leak', verdict: true },
  { key: 'FLAGGED', label: 'Flagged', verdict: true },
  { key: 'LEGACY', label: 'Legacy', verdict: true },
  { key: 'google_oauth', label: 'Google OAuth' },
  { key: 'magic_link', label: 'Magic link' },
  { key: 'nudge_dismissed_gt_3d', label: 'Dismissed > 3d ago' },
  { key: 'reminder_v1_sent', label: 'Reminder v1 sent' },
  { key: 'reminder_v2_deletion_pending', label: 'Deletion pending' },
];

const AdminIncompleteProfilesPage = () => {
  const { refetchLeakCount } = useAdminBadge();
  const [filter, setFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  // Bundle 164 A4 - unfiltered verdict counts for filter-chip
  // badges. Fetched once on mount + after backfill.
  const [totalByVerdict, setTotalByVerdict] = useState(null);
  // Bundle 164 hotfix F1 - super-admin can unmask emails.
  // Persisted per-page in localStorage.
  const [showEmails, setShowEmails] = useState(() => {
    try {
      return localStorage.getItem(
        'dostination.admin.show_emails') === '1';
    } catch (_) {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        'dostination.admin.show_emails',
        showEmails ? '1' : '0');
    } catch (_) { /* noop */ }
  }, [showEmails]);
  // Bundle 164 hotfix E - stale-flag backfill state (separate
  // from the Bundle 162 missing-flag backfill above).
  const [staleBackfillOpen, setStaleBackfillOpen] = useState(false);
  const [staleBackfillPreview, setStaleBackfillPreview] = useState(null);
  const [staleBackfillBusy, setStaleBackfillBusy] = useState(false);

  const loadTotalByVerdict = async () => {
    try {
      const { data } = await api.get(
        '/admin/users/incomplete-profiles-v2/total-by-verdict');
      setTotalByVerdict(data.by_verdict || null);
    } catch (_) {
      // noop - chips render without badges
    }
  };

  const load = async () => {
    try {
      // If a verdict filter is active or "all" is selected we
      // hit v2 (adds `verdict` per row + supports server-side
      // pagination). Otherwise fall through to the legacy
      // filter surface for reminder-status filters that were
      // built in Bundle 161.
      const isVerdict = filter === 'all'
        || ['LEAK', 'FLAGGED', 'LEGACY', 'COMPLETE'].includes(filter);
      const url = isVerdict
        ? `/admin/users/incomplete-profiles-v2?page=${page}&page_size=${pageSize}${filter !== 'all' ? `&verdict=${filter}` : ''}`
        : `/admin/users/incomplete-profiles?filter=${filter}&limit=200`;
      const { data } = await api.get(url);
      const raw = data.rows || [];
      // For legacy endpoint rows we derive verdict client-side
      // using the same logic (cutoff + dob + flag).
      const withVerdict = raw.map((r) => {
        if (r.verdict) return r;
        const post = (r.created_at || '') >= '2026-08-31';
        const hasDob = !!(r.profile && r.profile.dob);
        const v = r.signup_completion_required
          ? 'FLAGGED'
          : (!post ? 'LEGACY' : (hasDob ? 'COMPLETE' : 'LEAK'));
        return { ...r, verdict: v };
      });
      setRows(withVerdict);
      setTotal(data.total || withVerdict.length);
      setTotalPages(data.total_pages || 1);
      setSelected(new Set());
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to load rows.'));
    }
  };

  useEffect(() => { load(); }, [filter, page]);
  useEffect(() => { loadTotalByVerdict(); }, []);

  // Reset to page 1 when filter changes (avoids landing on
  // an empty page when the filter narrows the row count).
  useEffect(() => { setPage(1); }, [filter]);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAllVisible = () => {
    const ids = rows.filter((r) => !r.opted_out).map((r) => r.id);
    setSelected(new Set(ids));
  };

  const clearSelection = () => setSelected(new Set());

  const sendV1 = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(
        `/admin/users/${id}/send-completion-reminder-v1`);
      if (data.status === 'sent') toast.success('Reminder v1 sent.');
      else if (data.status === 'skipped') {
        toast.warning(`Skipped: ${data.reason}`);
      }
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Send failed.'));
    } finally { setBusyId(null); }
  };

  const sendV2 = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(
        `/admin/users/${id}/send-completion-reminder-v2`);
      if (data.status === 'sent') {
        toast.success('Reminder v2 sent. Deletion scheduled in 48h.');
      } else if (data.status === 'skipped') {
        toast.warning(`Skipped: ${data.reason}`);
      }
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Send failed.'));
    } finally { setBusyId(null); }
  };

  const bulkSend = async (variant) => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const path = variant === 'v1'
        ? '/admin/users/incomplete-profiles/bulk-send-v1'
        : '/admin/users/incomplete-profiles/bulk-send-v2';
      const { data } = await api.post(path,
        { ids: Array.from(selected) });
      toast.success(
        `Bulk ${variant}: sent ${data.sent}, skipped ${data.skipped}, errors ${data.errors}.`);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Bulk send failed.'));
    } finally { setBulkBusy(false); }
  };

  const openBackfill = async () => {
    setBackfillOpen(true);
    setBackfillPreview(null);
    setBackfillBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-signup-completion-flag',
        { apply: false });
      setBackfillPreview(data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Backfill preview failed.'));
      setBackfillOpen(false);
    } finally {
      setBackfillBusy(false);
    }
  };

  const applyBackfill = async () => {
    setBackfillBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-signup-completion-flag',
        { apply: true });
      toast.success(`Backfill applied: ${data.applied} row(s) stamped.`);
      setBackfillOpen(false);
      setBackfillPreview(null);
      await load();
      await loadTotalByVerdict();
      // Bundle 165 C - stamping flags changes LEAK count.
      refetchLeakCount();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Backfill apply failed.'));
    } finally {
      setBackfillBusy(false);
    }
  };

  const openStaleBackfill = async () => {
    setStaleBackfillOpen(true);
    setStaleBackfillPreview(null);
    setStaleBackfillBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-stale-completion-flags',
        { apply: false });
      setStaleBackfillPreview(data);
    } catch (e) {
      toast.error(extractErrorMessage(
        e, 'Stale-flag preview failed.'));
      setStaleBackfillOpen(false);
    } finally {
      setStaleBackfillBusy(false);
    }
  };

  const applyStaleBackfill = async () => {
    setStaleBackfillBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-stale-completion-flags',
        { apply: true });
      toast.success(
        `Stale flags cleared: ${data.applied} row(s).`);
      setStaleBackfillOpen(false);
      setStaleBackfillPreview(null);
      await load();
      await loadTotalByVerdict();
      // Bundle 165 C - clearing stale flags changes LEAK
      // (data-truth-wins re-evaluates the previously FLAGGED
      // rows).
      refetchLeakCount();
    } catch (e) {
      toast.error(extractErrorMessage(
        e, 'Stale-flag apply failed.'));
    } finally {
      setStaleBackfillBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    if (confirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    try {
      await api.post(
        `/admin/users/${deleteTarget.id}/delete-for-incomplete-profile`,
        { confirm: 'DELETE' });
      toast.success('User deleted; audit stub retained.');
      setDeleteTarget(null); setConfirmText('');
      await load();
      // Bundle 165 C - delete removes a row from the LEAK
      // cohort. Invalidate sidebar badge immediately.
      refetchLeakCount();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Delete failed.'));
    }
  };

  const deletableIds = useMemo(() => (
    new Set(rows.filter((r) => (
      r.deletion_scheduled_at
        && new Date(r.deletion_scheduled_at).getTime() <= Date.now()
    )).map((r) => r.id))
  ), [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="admin-incomplete-profiles-page">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-primary-800" />
        <h1 className="text-2xl font-semibold text-primary-900">
          Incomplete profiles
        </h1>
        <Badge variant="outline">{total} total</Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowEmails((v) => !v)}
            className="border-neutral-300"
            data-testid="ipp-toggle-show-emails"
            aria-pressed={showEmails}
          >
            {showEmails ? (
              <><EyeOff className="h-3.5 w-3.5 mr-1" />Hide emails</>
            ) : (
              <><Eye className="h-3.5 w-3.5 mr-1" />Show emails</>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openStaleBackfill}
            className="border-emerald-300 text-emerald-800"
            data-testid="ipp-stale-backfill-open"
          >
            <Wrench className="h-3.5 w-3.5 mr-1" />
            Clear stale flags
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openBackfill}
            className="border-primary-300 text-primary-900"
            data-testid="ipp-backfill-open"
          >
            <Wrench className="h-3.5 w-3.5 mr-1" />
            Backfill missing flags
          </Button>
        </div>
      </div>

      <VerdictLegend />

      <Card>
        <CardHeader><CardTitle className="text-sm">
          Manual escalation flow (per Bundle 161 spec)
        </CardTitle></CardHeader>
        <CardContent className="text-xs text-neutral-600 space-y-1">
          <div>1. Send Reminder v1 (gentle nudge).</div>
          <div>2. Wait 48 hours, then send Reminder v2 (2-day
                deletion warning).</div>
          <div>3. After deletion date passes, manually delete
                (audit stub retained).</div>
          <div>Opt-outs are honored automatically. Emails route
                through email_service (Resend + email_logs).</div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => {
          const badge = f.verdict && totalByVerdict
            ? totalByVerdict[f.key] : null;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={
                'rounded-full border px-3 py-1 text-xs inline-flex items-center gap-1 '
                + (filter === f.key
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400')
              }
              data-testid={`ipp-filter-${f.key}`}
            >
              <span>{f.label}</span>
              {badge !== null && badge !== undefined ? (
                <span
                  className={
                    'rounded-full text-[10px] px-1.5 py-0.5 ml-0.5 '
                    + (filter === f.key
                      ? 'bg-white/20 text-white'
                      : 'bg-neutral-100 text-neutral-700')
                  }
                  data-testid={`ipp-filter-${f.key}-count`}
                >
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline"
                onClick={selectAllVisible}
                data-testid="ipp-select-all">
          Select all visible ({rows.filter((r) => !r.opted_out).length})
        </Button>
        <Button size="sm" variant="ghost"
                onClick={clearSelection}
                disabled={!selected.size}
                data-testid="ipp-clear-selection">
          Clear
        </Button>
        <Button size="sm"
                onClick={() => bulkSend('v1')}
                disabled={bulkBusy || !selected.size}
                className="bg-primary-900 text-white hover:bg-primary-800"
                data-testid="ipp-bulk-v1">
          <Mail className="h-3.5 w-3.5 mr-1" />
          Send reminder v1 to selected ({selected.size})
        </Button>
        <Button size="sm"
                onClick={() => bulkSend('v2')}
                disabled={bulkBusy || !selected.size}
                className="bg-accent-700 text-white hover:bg-accent-800"
                data-testid="ipp-bulk-v2">
          <MailWarning className="h-3.5 w-3.5 mr-1" />
          Send reminder v2 to selected ({selected.size})
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="ipp-table">
          <thead className="bg-neutral-100">
            <tr>
              <th className="p-2 text-left"></th>
              <th className="p-2 text-left">Verdict</th>
              <th className="p-2 text-left">User</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-left">Signup</th>
              <th className="p-2 text-left">Why flagged</th>
              <th className="p-2 text-left">V1</th>
              <th className="p-2 text-left">V2</th>
              <th className="p-2 text-left">Delete on</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t"
                  data-testid={`ipp-row-${r.id}`}>
                <td className="p-2">
                  <Checkbox
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggleSelect(r.id)}
                    disabled={!!r.opted_out}
                    data-testid={`ipp-select-${r.id}`}
                  />
                </td>
                <td className="p-2" data-testid={`ipp-verdict-${r.id}`}>
                  <VerdictChip
                    verdict={r.verdict || 'LEGACY'}
                    testId={`ipp-verdict-chip-${r.id}`}
                  />
                </td>
                <td className="p-2 font-medium text-primary-900">
                  {(r.first_name && r.first_name.trim() && r.first_name !== '?')
                    ? (<span>{r.first_name} {r.last_name || ''}</span>)
                    : (
                      <em
                        className="text-neutral-400"
                        data-testid={`ipp-name-missing-${r.id}`}
                      >
                        (name missing)
                      </em>
                    )}
                </td>
                <td className="p-2 text-neutral-700">
                  <span data-testid={`ipp-email-${r.id}`}>
                    {showEmails
                      ? (r.email || r.email_masked)
                      : r.email_masked}
                  </span>
                  {r.opted_out ? (
                    <Badge className="ml-1 bg-red-100 text-red-800 border-red-200 text-[9px]">
                      OPTED OUT
                    </Badge>
                  ) : null}
                </td>
                <td className="p-2 text-neutral-600">{r.signup_source}</td>
                <td
                  className="p-2 text-neutral-600"
                  data-testid={`ipp-signup-time-${r.id}`}
                >
                  {formatRelativeTime(r.created_at)}
                </td>
                <td
                  className="p-2 text-neutral-500 font-mono text-[11px]"
                  data-testid={`ipp-reason-${r.id}`}
                >
                  {r.reason_v2 || r.signup_completion_reason || '-'}
                </td>
                <td
                  className="p-2 text-neutral-600"
                  data-testid={`ipp-v1-sent-${r.id}`}
                >
                  {r.reminder_v1_sent_at
                    ? `sent ${formatRelativeTime(r.reminder_v1_sent_at)}`
                    : '-'}
                </td>
                <td
                  className="p-2 text-neutral-600"
                  data-testid={`ipp-v2-sent-${r.id}`}
                >
                  {r.reminder_v2_sent_at
                    ? `sent ${formatRelativeTime(r.reminder_v2_sent_at)}`
                    : '-'}
                </td>
                <td className="p-2 text-neutral-600">
                  {r.deletion_scheduled_at
                    ? formatRelativeTime(r.deletion_scheduled_at)
                    : '-'}
                </td>
                <td className="p-2 space-x-1 whitespace-nowrap">
                  <Link
                    to={`/admin/users/${r.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-[10px] font-medium text-primary-900 hover:border-primary-400"
                    data-testid={`ipp-open-profile-${r.id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open profile
                  </Link>
                  <Button size="sm" variant="outline"
                          disabled={busyId === r.id || r.opted_out
                            || isWithin24hCooldown(r.reminder_v1_sent_at)}
                          onClick={() => sendV1(r.id)}
                          data-testid={`ipp-v1-${r.id}`}>
                    {r.reminder_v1_sent_at ? 'Resend V1' : 'V1'}
                  </Button>
                  <Button size="sm" variant="outline"
                          disabled={busyId === r.id || r.opted_out
                            || isWithin24hCooldown(r.reminder_v2_sent_at)}
                          onClick={() => sendV2(r.id)}
                          data-testid={`ipp-v2-${r.id}`}
                          className="border-accent-300 text-accent-800">
                    {r.reminder_v2_sent_at ? 'Resend V2' : 'V2'}
                  </Button>
                  {deletableIds.has(r.id) ? (
                    <Button size="sm"
                            onClick={() => { setDeleteTarget(r); setConfirmText(''); }}
                            className="bg-red-700 text-white hover:bg-red-800"
                            data-testid={`ipp-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={11}
                      className="p-6 text-center text-neutral-400"
                      data-testid="ipp-empty">
                No rows.
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div
        className="flex items-center justify-between gap-2 text-xs text-neutral-600"
        data-testid="ipp-pagination"
      >
        <div data-testid="ipp-pagination-summary">
          Showing {rows.length ? ((page - 1) * pageSize) + 1 : 0}
          {'-'}
          {((page - 1) * pageSize) + rows.length} of {total}
          {filter !== 'all' ? (
            <span className="ml-1 text-neutral-500">(filter: {filter})</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="ipp-pagination-prev"
          >
            Prev
          </Button>
          <span data-testid="ipp-pagination-page">
            Page {page} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="ipp-pagination-next"
          >
            Next
          </Button>
        </div>
      </div>

      <Dialog open={staleBackfillOpen}
                onOpenChange={(o) => { if (!o) { setStaleBackfillOpen(false); setStaleBackfillPreview(null); } }}>
        <DialogContent data-testid="ipp-stale-backfill-modal">
          <DialogHeader>
            <DialogTitle>Clear stale completion flags</DialogTitle>
            <DialogDescription>
              Bundle 164 hotfix. Sweeps users where
              <code> signup_completion_required=True </code>
              but the underlying data (DOB + last name) is
              already present. The flag is stale; the verdict
              was FLAGGED even though the profile is COMPLETE.
              Clears the flag + reason and stamps
              <code> signup_completed_at</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-neutral-700 space-y-2">
            {staleBackfillBusy && !staleBackfillPreview ? (
              <div data-testid="ipp-stale-backfill-loading">
                Running dry-run.
              </div>
            ) : staleBackfillPreview ? (
              <div
                data-testid="ipp-stale-backfill-preview"
                className="space-y-1"
              >
                <div>
                  Stale rows to clear:
                  {' '}
                  <span
                    className="font-mono font-semibold text-primary-900"
                    data-testid="ipp-stale-backfill-count"
                  >
                    {staleBackfillPreview.scanned}
                  </span>
                </div>
                {staleBackfillPreview.sample?.length ? (
                  <div className="mt-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 max-h-40 overflow-y-auto">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                      Sample (up to 10)
                    </div>
                    <ul className="space-y-0.5">
                      {staleBackfillPreview.sample.map((s) => (
                        <li key={s.id} className="font-mono text-[10px]">
                          {s.email} - {s.first_name} {s.last_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-neutral-500">
                    No stale flags found. Nothing to clear.
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setStaleBackfillOpen(false); setStaleBackfillPreview(null); }}
              data-testid="ipp-stale-backfill-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-700 text-white hover:bg-emerald-800"
              disabled={staleBackfillBusy || !staleBackfillPreview || !staleBackfillPreview.scanned}
              onClick={applyStaleBackfill}
              data-testid="ipp-stale-backfill-apply"
            >
              Clear ({staleBackfillPreview?.scanned ?? 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={backfillOpen}
                onOpenChange={(o) => { if (!o) { setBackfillOpen(false); setBackfillPreview(null); } }}>
        <DialogContent data-testid="ipp-backfill-modal">
          <DialogHeader>
            <DialogTitle>Backfill missing flags</DialogTitle>
            <DialogDescription>
              Stamps <code>signup_completion_required=True</code> on
              every user created on or after 2026-08-31 whose DOB is
              missing and who was not already flagged. Idempotent.
              Pre-Bundle-149 legacy users are excluded per policy.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-neutral-700 space-y-2">
            {backfillBusy && !backfillPreview ? (
              <div data-testid="ipp-backfill-loading">Running dry-run.</div>
            ) : backfillPreview ? (
              <div data-testid="ipp-backfill-preview" className="space-y-1">
                <div>
                  Scanned rows to stamp:
                  {' '}
                  <span className="font-mono font-semibold text-primary-900">
                    {backfillPreview.scanned}
                  </span>
                </div>
                <div className="text-neutral-500">
                  Cutoff since: {backfillPreview.since}
                </div>
                {backfillPreview.sample?.length ? (
                  <div className="mt-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 max-h-40 overflow-y-auto">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                      Sample (up to 10)
                    </div>
                    <ul className="space-y-0.5">
                      {backfillPreview.sample.map((s) => (
                        <li key={s.id} className="font-mono text-[10px]">
                          {s.email} - {s.created_via || s.provider || '?'} - {s.created_at?.slice(0, 10)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-neutral-500">
                    No rows would be stamped. Nothing to apply.
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setBackfillOpen(false); setBackfillPreview(null); }}
              data-testid="ipp-backfill-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-primary-900 text-white hover:bg-primary-800"
              disabled={backfillBusy || !backfillPreview || !backfillPreview.scanned}
              onClick={applyBackfill}
              data-testid="ipp-backfill-apply"
            >
              Apply ({backfillPreview?.scanned ?? 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget}
                onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setConfirmText(''); } }}>
        <DialogContent data-testid="ipp-delete-modal">
          <DialogHeader>
            <DialogTitle>Delete account?</DialogTitle>
            <DialogDescription>
              This permanently removes {deleteTarget?.first_name || 'this user'}
              &apos;s account. A GDPR audit stub is retained.
              Type <strong>DELETE</strong> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            data-testid="ipp-delete-confirm-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteTarget(null); setConfirmText(''); }}>
              Cancel
            </Button>
            <Button className="bg-red-700 text-white hover:bg-red-800"
                    onClick={doDelete}
                    data-testid="ipp-delete-submit">
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIncompleteProfilesPage;
